import React from 'react';
import { X } from 'lucide-react';

export const FeatureDetailModal = ({ isOpen, onClose, feature }) => {
  if (!isOpen || !feature) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg"
        style={{ backgroundColor: '#1A1F2E' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg transition-colors"
          style={{ 
            backgroundColor: '#2A3142',
            color: '#E6EDF3'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#3A4152'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#2A3142'}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Content */}
        <div className="p-8">
          {/* Header with Icon */}
          <div className="flex items-center gap-4 mb-6">
            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: '#2A3142' }}
            >
              {feature.icon}
            </div>
            <div>
              <h2 
                className="text-3xl font-bold mb-2"
                style={{ color: '#7CD4FD', fontFamily: 'Manrope' }}
              >
                {feature.title}
              </h2>
              <p style={{ color: '#8B92A7', fontSize: '16px' }}>
                {feature.subtitle}
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="mb-8">
            <p 
              style={{ 
                color: '#E6EDF3', 
                fontSize: '18px',
                lineHeight: '1.8'
              }}
            >
              {feature.description}
            </p>
          </div>

          {/* Key Features */}
          {feature.keyFeatures && feature.keyFeatures.length > 0 && (
            <div className="mb-8">
              <h3 
                className="text-xl font-bold mb-4"
                style={{ color: '#7CD4FD', fontFamily: 'Manrope' }}
              >
                Hauptfunktionen
              </h3>
              <div className="grid gap-3">
                {feature.keyFeatures.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 p-4 rounded-lg"
                    style={{ backgroundColor: '#2A3142' }}
                  >
                    <div 
                      className="mt-1"
                      style={{ 
                        color: '#7CD4FD',
                        fontSize: '20px',
                        fontWeight: 'bold'
                      }}
                    >
                      ✓
                    </div>
                    <div>
                      <h4 
                        className="font-semibold mb-1"
                        style={{ color: '#E6EDF3', fontSize: '16px' }}
                      >
                        {item.title}
                      </h4>
                      <p style={{ color: '#8B92A7', fontSize: '14px' }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Screenshots */}
          {feature.screenshots && feature.screenshots.length > 0 && (
            <div className="mb-8">
              <h3 
                className="text-xl font-bold mb-4"
                style={{ color: '#7CD4FD', fontFamily: 'Manrope' }}
              >
                In Aktion
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {feature.screenshots.map((screenshot, index) => (
                  <div 
                    key={index}
                    className="rounded-lg overflow-hidden"
                    style={{ backgroundColor: '#2A3142' }}
                  >
                    <img 
                      src={screenshot.url} 
                      alt={screenshot.caption}
                      className="w-full h-auto"
                      style={{ objectFit: 'cover' }}
                    />
                    {screenshot.caption && (
                      <p 
                        className="p-3 text-sm"
                        style={{ color: '#8B92A7' }}
                      >
                        {screenshot.caption}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* How it Works */}
          {feature.howItWorks && feature.howItWorks.length > 0 && (
            <div className="mb-8">
              <h3 
                className="text-xl font-bold mb-4"
                style={{ color: '#7CD4FD', fontFamily: 'Manrope' }}
              >
                So funktioniert's
              </h3>
              <div className="space-y-4">
                {feature.howItWorks.map((step, index) => (
                  <div 
                    key={index}
                    className="flex gap-4 p-4 rounded-lg"
                    style={{ backgroundColor: '#2A3142' }}
                  >
                    <div 
                      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold"
                      style={{ 
                        backgroundColor: '#7CD4FD',
                        color: '#0B0F14'
                      }}
                    >
                      {index + 1}
                    </div>
                    <div>
                      <h4 
                        className="font-semibold mb-1"
                        style={{ color: '#E6EDF3', fontSize: '16px' }}
                      >
                        {step.title}
                      </h4>
                      <p style={{ color: '#8B92A7', fontSize: '14px' }}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Benefits */}
          {feature.benefits && feature.benefits.length > 0 && (
            <div>
              <h3 
                className="text-xl font-bold mb-4"
                style={{ color: '#7CD4FD', fontFamily: 'Manrope' }}
              >
                Ihre Vorteile
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {feature.benefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: '#2A3142' }}
                  >
                    <div style={{ color: '#22C55E', fontSize: '20px' }}>
                      ●
                    </div>
                    <span style={{ color: '#E6EDF3', fontSize: '14px' }}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
